import {QueryFunction, QueryKey, UseQueryOptions, useQuery} from '@tanstack/react-query';

import {getQueryKey} from '../lib/helpers/queryKeys';
import {supabase} from '../lib/utils/supabaseClass';
import {limitT} from '../types/pagination.types';
import {BaseViewFilterT, NotViewFilterT, PaginationKeyT, ViewFilterT} from '../types/query.types';
import {ViewsT} from '../types/utility.types';
import {getQueryClient} from './queryClientFix';

/*********************************************************************
 * Helper Functions
 *********************************************************************/
// Function to validate the paginationKey
function isPaginationKey(value: string): value is PaginationKeyT {
  return value === 'all' || /^page:\d+ limit:\d+$/.test(value);
}

/*******************************************************************
 * Types
 *******************************************************************/
export type UseViewQueryResponseT<V extends keyof ViewsT> = {
  data: ViewsT[V]['Row'][];
  count: number | null;
  currentPage: number;
  totalPages: number;
};

export type OrderConfig<V extends keyof ViewsT> = {
  column: keyof ViewsT[V]['Row'];
  ascending?: boolean;
};

export type ViewSortOptionT<V extends keyof ViewsT> = {
  label: string;
  column: keyof ViewsT[V]['Row'];
  direction: 'asc' | 'desc';
  default?: boolean;
};

export type UseViewQueryPropsT<
  V extends keyof ViewsT,
  TData = UseViewQueryResponseT<V>,
  TError extends Error = Error,
  TQueryFnData = TData,
  TQueryKey extends QueryKey = QueryKey,
> = Omit<UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>, 'queryKey' | 'queryFn'> & {
  view: V;
  enabled?: boolean;
  filters?: ViewFilterT<V>[];
  searchTerm?: string;
  searchColumn?: keyof ViewsT[V]['Row'];
  single?: boolean;
  orderBy?: OrderConfig<V>;
  sortOption?: ViewSortOptionT<V>;
  page?: number;
  limit?: limitT;
  all?: boolean;
};

async function fetchView<V extends keyof ViewsT>({
  view,
  filters = [],
  searchTerm = '',
  searchColumn,
  single = false,
  orderBy,
  sortOption,
  page = null,
  limit = null,
  all = !page && !limit,
}: {
  view: V;
  filters?: ViewFilterT<V>[];
  searchTerm?: string;
  searchColumn?: keyof ViewsT[V]['Row'];
  single?: boolean;
  orderBy?: OrderConfig<V>;
  sortOption?: ViewSortOptionT<V>;
  page?: number | null;
  limit?: limitT | null;
  all?: boolean;
}): Promise<UseViewQueryResponseT<V>> {
  let query = supabase.from(view).select('*', {count: 'exact'});

  // Apply search if searchTerm and searchColumn are provided
  if (searchTerm && searchColumn) {
    query = query.ilike(searchColumn as string, `%${searchTerm}%`);
  }

  // Update filter handling
  filters.forEach(filter => {
    function isBaseFilter(filter: ViewFilterT<V>): filter is BaseViewFilterT<V> {
      return 'column' in filter && 'operator' in filter && 'value' in filter;
    }

    function isNotFilter(filter: ViewFilterT<V>): filter is NotViewFilterT<V> {
      return 'not' in filter;
    }

    if (isNotFilter(filter) && filter.not) {
      // Handle NOT filter - modified to use proper Supabase syntax
      const {column, operator, value} = filter.not;
      const columnName = String(column);

      if (operator === 'in' && Array.isArray(value)) {
        // For 'not in' queries, we need to use a different syntax
        query = query.not(columnName, 'in', `(${value.join(',')})`);
      } else {
        query = query.not(columnName, operator, value);
      }
    } else if (isBaseFilter(filter)) {
      const {column, operator, value} = filter;
      const columnName = String(column);

      switch (operator) {
        case 'eq':
          // @ts-ignore - TODO: fix this, value cannot be correctly typed
          query = query.eq(columnName, value);
          break;
        case 'neq':
          // @ts-ignore - TODO: fix this, value cannot be correctly typed
          query = query.neq(columnName, value);
          break;
        case 'gt':
          query = query.gt(columnName, value);
          break;
        case 'gte':
          query = query.gte(columnName, value);
          break;
        case 'lt':
          query = query.lt(columnName, value);
          break;
        case 'lte':
          query = query.lte(columnName, value);
          break;
        case 'in':
          if (Array.isArray(value)) {
            // @ts-ignore - TODO: fix this, value cannot be correctly typed
            query = query.in(columnName, value);
          }
          break;
        case 'is':
          query = query.is(columnName, value as boolean | null);
          break;
        case 'containedBy':
          // For JSON array containment
          if (typeof value === 'string') {
            try {
              const parsedValue = JSON.parse(value);
              query = query.contains(columnName, parsedValue);
            } catch (e) {
              console.error('Invalid JSON in containedBy filter:', e);
            }
          } else if (Array.isArray(value)) {
            // If value is already an array, convert it to a JSON string first
            query = query.contains(columnName, JSON.stringify(value));
          } else {
            // If value is an object, wrap it in an array and stringify
            query = query.contains(columnName, JSON.stringify([value]));
          }
          break;
      }
    }
  });

  // Update sorting logic to use sortOption if provided, fallback to orderBy
  if (sortOption) {
    query = query.order(sortOption.column as string, {
      ascending: sortOption.direction === 'asc',
    });
  } else if (orderBy) {
    query = query.order(orderBy.column as string, {ascending: orderBy.ascending});
  }

  // Apply pagination if not fetching all
  if (!all && page !== null && limit !== null) {
    query = query.range((page - 1) * limit, page * limit - 1);
  }

  const {data, error, count} = single ? await query.single() : await query;

  if (error) throw error;
  if (!data) throw new Error('No data returned');

  const typedData = data as unknown as ViewsT[V]['Row'] | ViewsT[V]['Row'][];
  const totalPages = limit ? Math.ceil((count ?? 0) / limit) : 1;

  return {
    data: Array.isArray(typedData) ? typedData : [typedData],
    count,
    currentPage: page ?? 1,
    totalPages,
  };
}

// Add this type for view query parameters
export type ViewQueryParamsT<V extends keyof ViewsT> = {
  enabled: boolean;
  filters: ViewFilterT<V>[];
  searchTerm: string;
  searchColumn?: keyof ViewsT[V]['Row'];
  single: boolean;
  orderBy?: OrderConfig<V>;
  // Add pagination params
  page?: number;
  limit?: limitT;
  all?: boolean;
};

export const useViewQuery = <V extends keyof ViewsT>({
  view,
  enabled = true,
  filters = [],
  searchTerm = '',
  searchColumn,
  single = false,
  orderBy,
  sortOption,
  page = undefined,
  limit = undefined,
  all = (!page && !limit) ?? true,
  ...rest
}: UseViewQueryPropsT<V>): ReturnType<
  typeof useQuery<UseViewQueryResponseT<V>, Error, UseViewQueryResponseT<V>, QueryKey>
> & {
  pagination: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
  };
} => {
  const paginationKey = page !== undefined && limit !== undefined ? `page:${page} limit:${limit}` : 'all';
  if (!isPaginationKey(paginationKey)) throw new Error('Invalid pagination key');

  const queryKey = getQueryKey.view(view, {
    enabled,
    filters,
    searchTerm,
    searchColumn,
    single,
    orderBy,
    sortOption,
    page,
    limit,
    all,
  });

  const queryFn: QueryFunction<UseViewQueryResponseT<V>, QueryKey> = async () => {
    return fetchView({
      view,
      filters,
      searchTerm,
      searchColumn,
      single,
      orderBy,
      sortOption,
      page,
      limit,
      all,
    });
  };

  const queryOptions: UseQueryOptions<UseViewQueryResponseT<V>, Error, UseViewQueryResponseT<V>, QueryKey> = {
    enabled,
    refetchOnMount: false,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    retryOnMount: false,
    ...rest,
    queryKey,
    queryFn,
  };

  // Try to use the normal React Query hook, but catch the "No QueryClient set" error
  // and use our fallback global QueryClient if that happens
  try {
    const queryResult = useQuery(queryOptions);

    return {
      ...queryResult,
      pagination: {
        currentPage: queryResult.data?.currentPage ?? 1,
        totalPages: queryResult.data?.totalPages ?? 1,
        totalItems: queryResult.data?.count ?? 0,
      },
    };
  } catch (error) {
    // Check if the error is about missing QueryClient
    if (error instanceof Error && error.message.includes('No QueryClient set')) {
      // Use the global QueryClient as fallback
      const queryClient = getQueryClient();
      // Try to get existing query data or fetch it
      const cachedData = queryClient.getQueryCache().find({queryKey});

      if (!cachedData) {
        // Trigger the query but don't wait for it
        queryClient
          .fetchQuery({...queryOptions, queryKey, queryFn})
          .catch(fetchError => console.error('Error fetching data:', fetchError));
      }

      // Return a format compatible with useQuery result
      return {
        data: cachedData?.state.data as UseViewQueryResponseT<V> | undefined,
        error: null,
        isError: false,
        isLoading: true,
        isSuccess: !!cachedData?.state.data,
        status: cachedData?.state.data ? 'success' : 'pending',
        // @ts-expect-error - TODO: fix this
        refetch: async () => queryClient.fetchQuery({queryKey, queryFn, ...queryOptions}),
        // Other properties that useQuery would normally provide
        pagination: {
          currentPage: 1,
          totalPages: 1,
          totalItems: 0,
        },
      };
    }

    // Re-throw any other errors
    throw error;
  }
};
